// /src/backendServices/fileManagment/uploadWithStore.ts


import { GenericFileUploader, UploadConfig, UploadCallbacks, UploadMetadata } from './api';
import { FileType, UploadStatus } from '../../store/fileManagment/types';
import { 
  startUpload,
  updateUploadProgress,
  updateUploadStatus,
  completeUpload,
  failUpload,
  setUploadChunkInfo,
  pauseUpload,
  resumeUpload,
  cancelUpload
} from '../../store/fileManagment/actions';
import { store } from '../../store/store';

// Fonction utilitaire pour déterminer le type de fichier basé sur le MIME type
const getFileTypeFromMimeType = (mimeType: string): FileType => {
  if (mimeType.startsWith('video/')) return FileType.VIDEO;
  if (mimeType.startsWith('image/')) return FileType.IMAGE;
  if (mimeType.startsWith('audio/')) return FileType.AUDIO;
  if (mimeType === 'application/pdf') return FileType.DOCUMENT;
  if (mimeType.startsWith('application/') && 
      (mimeType.includes('msword') || mimeType.includes('sheet') || mimeType.includes('presentation'))) {
    return FileType.DOCUMENT;
  }
  if (mimeType === 'application/x-executable' || mimeType === 'application/octet-stream') {
    return FileType.EXECUTABLE;
  }
  return FileType.OTHER;
};

// Fonction utilitaire pour calculer la vitesse d'upload
class UploadSpeedCalculator {
  private startTime: number;
  private lastUpdateTime: number;
  private lastUploadedBytes: number;
  private speedHistory: number[] = [];
  private readonly maxHistorySize = 10;

  constructor() {
    this.startTime = Date.now();
    this.lastUpdateTime = this.startTime;
    this.lastUploadedBytes = 0;
  }

  updateProgress(uploadedBytes: number, totalBytes: number): { speed: number; estimatedTimeRemaining: number } {
    const now = Date.now();
    const timeDiff = now - this.lastUpdateTime;
    
    if (timeDiff < 1000) { // Attendre au moins 1 seconde
      return { speed: 0, estimatedTimeRemaining: 0 };
    }

    const bytesDiff = uploadedBytes - this.lastUploadedBytes;
    const currentSpeed = bytesDiff / (timeDiff / 1000); // bytes per second

    // Maintenir un historique pour lisser la vitesse
    this.speedHistory.push(currentSpeed);
    if (this.speedHistory.length > this.maxHistorySize) {
      this.speedHistory.shift();
    }

    // Calculer la vitesse moyenne
    const averageSpeed = this.speedHistory.reduce((sum, speed) => sum + speed, 0) / this.speedHistory.length;
    
    // Calculer le temps restant
    const remainingBytes = totalBytes - uploadedBytes;
    const estimatedTimeRemaining = averageSpeed > 0 ? remainingBytes / averageSpeed : 0;

    this.lastUpdateTime = now;
    this.lastUploadedBytes = uploadedBytes;

    return {
      speed: Math.max(0, averageSpeed),
      estimatedTimeRemaining: Math.max(0, estimatedTimeRemaining)
    };
  }
}

// Interface pour la configuration d'upload avec store
export interface UploadWithStoreConfig extends UploadConfig {
  entityId?: number;
  entityType?: string;
  autoStart?: boolean;
  metadata?: UploadMetadata;
}

// Classe d'upload intégrée avec le store Redux
export class FileUploaderWithStore {
  private uploader: GenericFileUploader;
  private uploadId: string;
  private speedCalculator: UploadSpeedCalculator;
  private isPaused: boolean = false;
  private isCancelled: boolean = false;
  private file: File;

  constructor(
    file: File,
    config: UploadWithStoreConfig,
    uploadId?: string
  ) {
    this.file = file;
    this.uploadId = uploadId || `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.speedCalculator = new UploadSpeedCalculator();

    // Calculer les informations sur les chunks
    const chunkSize = config.chunkSize || (10 * 1024 * 1024);
    const totalChunks = Math.ceil(file.size / chunkSize);
    const fileType = getFileTypeFromMimeType(file.type || 'application/octet-stream');

    // Initialiser l'upload dans le store
    store.dispatch(startUpload(
      this.uploadId,
      file.name,
      file.size,
      fileType,
      file.type || 'application/octet-stream',
      totalChunks,
      chunkSize,
      config.maxRetries || 3,
      config.metadata,
      config.entityId,
      config.entityType
    ));

    // Créer les callbacks pour l'uploader
    const callbacks: UploadCallbacks = {
      onProgress: (progress: number) => this.handleProgress(progress),
      onChunkUploaded: (chunkIndex: number, totalChunks: number) => this.handleChunkUploaded(chunkIndex, totalChunks),
      onError: (error: Error, chunkIndex?: number) => this.handleError(error, chunkIndex),
      onComplete: () => this.handleComplete()
    };

    // Créer l'uploader générique
    this.uploader = new GenericFileUploader(config, callbacks);

    // Démarrer automatiquement si demandé
    if (config.autoStart !== false) {
      this.start(config.metadata);
    }
  }

  // Méthode pour démarrer l'upload
  async start(metadata?: UploadMetadata): Promise<void> {
    if (this.isCancelled || this.isPaused) return;

    try {
      store.dispatch(updateUploadStatus(this.uploadId, UploadStatus.UPLOADING));
      await this.uploader.uploadFile(this.file, this.uploadId, metadata);
    } catch (error) {
      if (!this.isCancelled) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        store.dispatch(failUpload(this.uploadId, errorMessage));
      }
    }
  }

  // Méthode pour mettre en pause l'upload
  pause(): void {
    this.isPaused = true;
    store.dispatch(pauseUpload(this.uploadId));
  }

  // Méthode pour reprendre l'upload
  resume(metadata?: UploadMetadata): void {
    if (this.isCancelled) return;
    
    this.isPaused = false;
    store.dispatch(resumeUpload(this.uploadId));
    this.start(metadata);
  }

  // Méthode pour annuler l'upload
  cancel(): void {
    this.isCancelled = true;
    this.uploader.cancelUpload(this.uploadId);
    store.dispatch(cancelUpload(this.uploadId));
  }

  // Gestion de la progression
  private handleProgress(progress: number): void {
    if (this.isCancelled || this.isPaused) return;

    const uploadedBytes = Math.round((progress / 100) * this.file.size);
    const { speed, estimatedTimeRemaining } = this.speedCalculator.updateProgress(uploadedBytes, this.file.size);

    store.dispatch(updateUploadProgress(
      this.uploadId,
      progress,
      uploadedBytes,
      speed,
      estimatedTimeRemaining
    ));
  }

  // Gestion de l'upload d'un chunk
  private handleChunkUploaded(chunkIndex: number, totalChunks: number): void {
    if (this.isCancelled) return;

    store.dispatch(setUploadChunkInfo(this.uploadId, chunkIndex, true));
  }

  // Gestion des erreurs
  private handleError(error: Error, chunkIndex?: number): void {
    if (this.isCancelled) return;

    console.error(`Upload error for ${this.uploadId}:`, error, { chunkIndex });
    
    // Si c'est une erreur sur un chunk spécifique, marquer le chunk comme échoué
    if (typeof chunkIndex === 'number') {
      store.dispatch(setUploadChunkInfo(this.uploadId, chunkIndex, false, 1));
    }
    
    // Ne pas marquer comme échoué immédiatement - laisser le retry se faire
    store.dispatch(updateUploadStatus(this.uploadId, UploadStatus.RETRYING));
  }

  // Gestion de la completion
  private handleComplete(): void {
    if (this.isCancelled) return;
    store.dispatch(completeUpload(this.uploadId));
  }

  // Getter pour l'ID d'upload
  get id(): string {
    return this.uploadId;
  }
}

// Fonction helper pour créer et démarrer un upload
export const createUpload = (
  file: File,
  config: UploadWithStoreConfig
): FileUploaderWithStore => {
  return new FileUploaderWithStore(file, config);
};

// Fonction helper pour créer plusieurs uploads en lot
export const createBatchUpload = (
  files: File[],
  config: UploadWithStoreConfig
): FileUploaderWithStore[] => {
  return files.map(file => new FileUploaderWithStore(file, {
    ...config,
    autoStart: false // Ne pas démarrer automatiquement pour permettre la gestion des lots
  }));
};

// Gestionnaire global pour les uploads
export class UploadManager {
  private static instance: UploadManager;
  private activeUploaders: Map<string, FileUploaderWithStore> = new Map();

  private constructor() {}

  static getInstance(): UploadManager {
    if (!UploadManager.instance) {
      UploadManager.instance = new UploadManager();
    }
    return UploadManager.instance;
  }

  // Ajouter un upload au gestionnaire
  addUpload(file: File, config: UploadWithStoreConfig): string {
    const uploader = new FileUploaderWithStore(file, config);
    this.activeUploaders.set(uploader.id, uploader);
    return uploader.id;
  }

  // Mettre en pause un upload
  pauseUpload(uploadId: string): boolean {
    const uploader = this.activeUploaders.get(uploadId);
    if (uploader) {
      uploader.pause();
      return true;
    }
    return false;
  }

  // Reprendre un upload
  resumeUpload(uploadId: string, metadata?: UploadMetadata): boolean {
    const uploader = this.activeUploaders.get(uploadId);
    if (uploader) {
      uploader.resume(metadata);
      return true;
    }
    return false;
  }

  // Annuler un upload
  cancelUpload(uploadId: string): boolean {
    const uploader = this.activeUploaders.get(uploadId);
    if (uploader) {
      uploader.cancel();
      this.activeUploaders.delete(uploadId);
      return true;
    }
    return false;
  }

  // Annuler tous les uploads
  cancelAllUploads(): void {
    this.activeUploaders.forEach((uploader) => {
      uploader.cancel();
    });
    this.activeUploaders.clear();
  }

  // Nettoyer les uploads terminés
  cleanupCompletedUploads(): void {
    const state = store.getState().fileUploadReducer;
    this.activeUploaders.forEach((uploader, uploadId) => {
      const uploadState = state.uploads[uploadId];
      if (uploadState && [UploadStatus.COMPLETED, UploadStatus.CANCELLED, UploadStatus.FAILED].includes(uploadState.status)) {
        this.activeUploaders.delete(uploadId);
      }
    });
  }
}

// Instance globale du gestionnaire
export const uploadManager = UploadManager.getInstance();
